import React, { useState } from "react";
import { useQuery } from "react-query";
import styled from "styled-components";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import {
  FiSearch,
  FiFilter,
  FiEye,
  FiEdit,
  FiTrash2,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiMapPin,
  FiPhone,
  FiMail,
  FiUsers,
  FiDollarSign,
  FiTrendingUp,
  FiDownload,
  FiRefreshCw,
  FiPlus,
  FiX,
  FiSend,
} from "react-icons/fi";
import { vendorsAPI } from "../../services/api";

const VendorManagementContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 0;
  overflow-x: hidden;
`;

const VendorManagementContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
  width: 100%;
  overflow-x: hidden;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 1.125rem;
  }

  .header-actions {
    display: flex;
    gap: 1rem;
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid #2d5016;
  background: ${(props) => (props.$primary ? "#2d5016" : "white")};
  color: ${(props) => (props.$primary ? "white" : "#2d5016")};
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.$primary ? "#1a3a0e" : "#2d5016")};
    color: white;
  }
`;

const FiltersSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 150px;

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #333;
  }

  input,
  select {
    padding: 0.75rem;
    border: 2px solid #e0e0e0;
    border-radius: 6px;
    font-size: 0.875rem;
    background: white;

    &:focus {
      outline: none;
      border-color: #2d5016;
    }
  }
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;

  input {
    width: 100%;
    padding-left: 2.5rem;
  }

  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
    font-size: 1.125rem;
  }
`;

const VendorsTable = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  overflow-x: auto;

  @media (max-width: 1200px) {
    overflow-x: auto;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 600;
  color: #2d5016;
  font-size: 0.875rem;
  min-width: 1100px;

  @media (max-width: 1200px) {
    min-width: 1100px;
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

const VendorRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  align-items: center;
  transition: background 0.3s ease;
  min-width: 1100px;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 1200px) {
    min-width: 1100px;
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

const VendorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  .vendor-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2d5016, #4a7c59);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 0.875rem;
  }

  .vendor-details {
    h3 {
      font-size: 1rem;
      font-weight: 600;
      color: #2d5016;
      margin-bottom: 0.25rem;
    }

    .cuisine {
      color: #666;
      font-size: 0.875rem;
    }
  }

  @media (max-width: 1024px) {
    margin-bottom: 1rem;
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;

  &.pending {
    background: #fff3cd;
    color: #856404;
  }

  &.approved {
    background: #d4edda;
    color: #155724;
  }

  &.rejected {
    background: #f8d7da;
    color: #721c24;
  }

  &.suspended {
    background: #f8d7da;
    color: #721c24;
  }
`;

const LoginStatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;

  &.logged-in {
    background: #d4edda;
    color: #155724;
  }

  &.not-logged-in {
    background: #fff3cd;
    color: #856404;
  }
`;

const ContactInfo = styled.div`
  color: #666;
  font-size: 0.875rem;

  .contact-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  @media (max-width: 1024px) {
    margin-bottom: 1rem;
  }
`;

const StatsInfo = styled.div`
  color: #666;
  font-size: 0.875rem;

  .stat-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  @media (max-width: 1024px) {
    margin-bottom: 1rem;
  }
`;

const ActionsColumn = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;

  @media (max-width: 1024px) {
    justify-content: flex-start;
  }
`;

const ActionIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e0e0e0;
  background: white;
  color: #666;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #2d5016;
    color: #2d5016;
  }

  &.approve:hover {
    border-color: #28a745;
    color: #28a745;
  }

  &.reject:hover {
    border-color: #dc3545;
    color: #dc3545;
  }

  &.delete:hover {
    border-color: #dc3545;
    color: #dc3545;
  }

  &.email:hover {
    border-color: #2d5016;
    color: #2d5016;
  }
`;

const MobileVendorCard = styled.div`
  display: none;

  @media (max-width: 1024px) {
    display: block;
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: start;
      margin-bottom: 1rem;
    }

    .card-content {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
      margin-bottom: 1rem;
    }

    .card-actions {
      display: flex;
      gap: 0.5rem;
      justify-content: flex-end;
    }
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #666;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;

  h3 {
    margin-bottom: 1rem;
    color: #333;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const Modal = styled(motion.div)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2d5016;
    margin: 0;
  }

  .close-button {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #666;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 50%;
    transition: all 0.3s ease;

    &:hover {
      background: #f8f9fa;
      color: #2d5016;
    }
  }
`;

const ModalContent = styled.div`
  padding: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #333;
  }

  input,
  select,
  textarea {
    padding: 0.75rem;
    border: 2px solid #e0e0e0;
    border-radius: 6px;
    font-size: 0.875rem;
    background: white;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: #2d5016;
    }

    &.error {
      border-color: #dc3545;
    }
  }

  textarea {
    resize: vertical;
    min-height: 80px;
  }

  .error-message {
    color: #dc3545;
    font-size: 0.75rem;
    margin-top: 0.25rem;
  }
`;

const ModalFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid #2d5016;
  background: ${(props) => (props.primary ? "#2d5016" : "white")};
  color: ${(props) => (props.primary ? "white" : "#2d5016")};
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.primary ? "#1a3a0e" : "#2d5016")};
    color: white;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const VendorManagement = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [cuisineFilter, setCuisineFilter] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if user can delete (only admins)
  const canDelete = user?.role === "admin";
  const [formData, setFormData] = useState({
    businessName: "",
    businessEmail: "",
    businessPhone: "",
  });
  const [errors, setErrors] = useState({});

  // Fetch real data from API
  const {
    data: vendorsData,
    isLoading: vendorsLoading,
    error: vendorsError,
    refetch,
  } = useQuery(
    "admin-all-vendors",
    async () => {
      console.log("Making API call to get vendors...");
      const result = await vendorsAPI.getVendors({
        status: statusFilter || undefined,
        cuisine: cuisineFilter || undefined,
        search: searchTerm || undefined,
      });
      console.log("API call result:", result);
      return result;
    },
    {
      keepPreviousData: true,
    }
  );

  const vendors = vendorsData?.data?.data?.vendors || [];

  console.log("Vendors data:", vendorsData);
  console.log("Vendors array:", vendors);
  console.log("Status filter:", statusFilter);
  console.log("Cuisine filter:", cuisineFilter);
  console.log("Search term:", searchTerm);

  // Temporarily disable client-side filtering to debug
  const filteredVendors = vendors;

  // const filteredVendors = vendors.filter((vendor) => {
  //   const matchesSearch = vendor.businessName
  //     .toLowerCase()
  //     .includes(searchTerm.toLowerCase());
  //   const matchesStatus = !statusFilter || vendor.status === statusFilter;
  //   const matchesCuisine =
  //     !cuisineFilter || vendor.cuisineType === cuisineFilter;

  //   return matchesSearch && matchesStatus && matchesCuisine;
  // });

  console.log("Filtered vendors:", filteredVendors);

  const handleVendorAction = async (vendorId, action) => {
    try {
      if (action === "view") {
        const vendor = vendors.find((v) => v.id === vendorId);
        setSelectedVendor(vendor);
        setShowViewModal(true);
      } else if (action === "edit") {
        const vendor = vendors.find((v) => v.id === vendorId);
        setSelectedVendor(vendor);
        setFormData({
          businessName: vendor.businessName || "",
          businessType: vendor.businessType || "",
          businessEmail: vendor.businessEmail || "",
          businessPhone: vendor.businessPhone || "",
          businessAddress: vendor.businessAddress || "",
          cuisineType: vendor.cuisineType || "",
          description: vendor.description || "",
          ownerName: vendor.ownerName || "",
          ownerEmail: vendor.ownerEmail || "",
          ownerPhone: vendor.ownerPhone || "",
        });
        setShowEditModal(true);
      } else if (action === "approve") {
        await vendorsAPI.approveVendor(vendorId);
        toast.success("Vendor approved successfully!");
        refetch();
      } else if (action === "reject") {
        await vendorsAPI.rejectVendor(vendorId);
        toast.success("Vendor rejected successfully!");
        refetch();
      } else if (action === "suspend") {
        await vendorsAPI.suspendVendor(vendorId);
        toast.success("Vendor suspended successfully!");
        refetch();
      } else if (action === "send-email") {
        try {
          const response = await vendorsAPI.sendWelcomeEmail(vendorId);

          // Check if vendor has already logged in
          console.log("Send welcome email response:", response.data);
          if (
            response.data?.hasLoggedIn === false ||
            !response.data?.hasLoggedIn
          ) {
            // Show success toast with temporary password
            toast.success("Welcome email sent successfully!", {
              duration: 6000,
            });

            // Show temporary password in a custom toast
            if (response.data?.temporaryPassword) {
              toast(
                (t) => (
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
                      🔐 Temporary Login Credentials
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      <strong>Email:</strong>{" "}
                      {vendors.find((v) => v.id === vendorId)?.businessEmail ||
                        vendors.find((v) => v.id === vendorId)?.ownerEmail}
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <strong>Password:</strong>{" "}
                      {response.data.temporaryPassword}
                    </div>
                    <div style={{ fontSize: "0.9em", color: "#666" }}>
                      ✅ Welcome email sent with these credentials
                    </div>
                    <div
                      style={{
                        fontSize: "0.8em",
                        color: "#999",
                        marginTop: "4px",
                      }}
                    >
                      💡 You can send this email again until the vendor logs in
                    </div>
                  </div>
                ),
                {
                  duration: 12000,
                  style: {
                    background: "#2d5016",
                    color: "#fff",
                    maxWidth: "400px",
                  },
                }
              );
            }
          } else {
            // Vendor has already logged in
            toast.error(
              "This vendor has already logged in. Welcome email cannot be sent again.",
              {
                duration: 8000,
              }
            );
          }
        } catch (error) {
          if (
            error.response?.status === 400 &&
            error.response?.data?.data?.hasLoggedIn
          ) {
            // Vendor has already logged in
            toast.error(
              "This vendor has already logged in. Welcome email cannot be sent again.",
              {
                duration: 8000,
              }
            );
          } else {
            throw error; // Re-throw other errors
          }
        }
      } else if (action === "delete") {
        // Show a more elegant confirmation using toast
        const confirmDelete = async () => {
          await vendorsAPI.deleteVendor(vendorId);
          toast.success("Vendor deleted successfully!");
          refetch();
        };

        // Create a custom confirmation toast
        toast(
          (t) => (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              <div style={{ fontWeight: "bold" }}>Delete Vendor</div>
              <div>
                Are you sure you want to delete this vendor? This action cannot
                be undone.
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={() => {
                    toast.dismiss(t.id);
                  }}
                  style={{
                    padding: "5px 15px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    background: "white",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    toast.dismiss(t.id);
                    confirmDelete();
                  }}
                  style={{
                    padding: "5px 15px",
                    border: "none",
                    borderRadius: "4px",
                    background: "#dc3545",
                    color: "white",
                    cursor: "pointer",
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ),
          {
            duration: Infinity, // Keep the toast open until user clicks
            id: "delete-vendor-confirmation",
          }
        );
      }
    } catch (error) {
      console.error(`Error ${action}ing vendor:`, error);
      toast.error(`Error ${action}ing vendor: ${error.message}`);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.businessName.trim()) {
      newErrors.businessName = "Business name is required";
    }
    if (!formData.businessEmail.trim()) {
      newErrors.businessEmail = "Business email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.businessEmail)) {
      newErrors.businessEmail = "Please enter a valid email";
    }
    if (!formData.businessPhone.trim()) {
      newErrors.businessPhone = "Business phone is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (showEditModal && selectedVendor) {
        // Convert camelCase to snake_case for backend compatibility
        // For editing, include all fields (vendor may have completed their profile)
        const updateData = {
          business_name: formData.businessName,
          business_type: formData.businessType || null,
          business_email: formData.businessEmail,
          business_phone: formData.businessPhone,
          business_address: formData.businessAddress || null,
          cuisine_type: formData.cuisineType || null,
          description: formData.description || null,
          owner_name: formData.ownerName || null,
          owner_email: formData.ownerEmail || null,
          owner_phone: formData.ownerPhone || null,
        };

        console.log("Updating vendor with data:", updateData);
        await vendorsAPI.updateVendor(selectedVendor.id, updateData);
        setShowEditModal(false);
        toast.success("Vendor updated successfully!");
      } else {
        console.log("Creating vendor with data:", formData);
        // Only send the required fields - don't send empty optional fields
        const vendorData = {
          businessName: formData.businessName,
          businessEmail: formData.businessEmail,
          businessPhone: formData.businessPhone,
        };
        const response = await vendorsAPI.createVendor(vendorData);
        setShowAddModal(false);

        // Show success toast with credentials
        if (response.data?.data?.temporaryPassword) {
          // Show success toast
          toast.success("Vendor created successfully!", {
            duration: 6000,
          });

          // Show detailed credentials in a custom toast
          toast(
            (t) => (
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
                  🔐 Temporary Login Credentials
                </div>
                <div style={{ marginBottom: "4px" }}>
                  <strong>Email:</strong> {formData.businessEmail}
                </div>
                <div style={{ marginBottom: "8px" }}>
                  <strong>Password:</strong>{" "}
                  {response.data.data.temporaryPassword}
                </div>
                <div
                  style={{ fontSize: "0.9em", color: "#666", marginTop: "8px" }}
                >
                  💡 Use the "Send Welcome Email" button to send onboarding info
                  to the vendor.
                </div>
              </div>
            ),
            {
              duration: 10000,
              style: {
                background: "#2d5016",
                color: "#fff",
                maxWidth: "400px",
              },
            }
          );
        } else {
          toast.success("Vendor created successfully!");
        }
      }

      setFormData({
        businessName: "",
        businessEmail: "",
        businessPhone: "",
      });
      setErrors({});
      setSelectedVendor(null);
      refetch();
    } catch (error) {
      console.error("Error saving vendor:", error);
      console.error("Error response:", error.response?.data);
      if (error.response?.data?.errors) {
        console.error("Validation errors:", error.response.data.errors);
        toast.error(
          `Validation failed: ${error.response.data.errors.map((e) => e.msg).join(", ")}`
        );
      } else {
        toast.error(`Error saving vendor: ${error.message}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setShowViewModal(false);
    setShowEditModal(false);
    setFormData({
      businessName: "",
      businessType: "",
      businessEmail: "",
      businessPhone: "",
      businessAddress: "",
      cuisineType: "",
      description: "",
      ownerName: "",
      ownerEmail: "",
      ownerPhone: "",
    });
    setErrors({});
    setSelectedVendor(null);
  };

  const cuisineTypes = [
    "Guyanese",
    "Indian",
    "Chinese",
    "Caribbean",
    "Creole",
    "International",
    "Mixed",
  ];

  const businessTypes = [
    "Restaurant",
    "Food Truck",
    "Catering",
    "Bakery",
    "Cafe",
    "Fast Food",
    "Fine Dining",
    "Street Food",
    "Other",
  ];

  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "suspended", label: "Suspended" },
  ];

  return (
    <VendorManagementContainer>
      <VendorManagementContent>
        <PageHeader>
          <div>
            <h1>Vendor Management</h1>
            <p>Manage vendor applications, approvals, and performance</p>
          </div>
          <div className="header-actions">
            <ActionButton $primary onClick={() => setShowAddModal(true)}>
              <FiPlus />
              Add New Vendor
            </ActionButton>
            <ActionButton>
              <FiDownload />
              Export Data
            </ActionButton>
            <ActionButton>
              <FiRefreshCw />
              Refresh
            </ActionButton>
          </div>
        </PageHeader>

        <FiltersSection>
          <FiltersRow>
            <SearchBar>
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBar>

            <FilterGroup>
              <label>Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FilterGroup>

            <FilterGroup>
              <label>Cuisine Type</label>
              <select
                value={cuisineFilter}
                onChange={(e) => setCuisineFilter(e.target.value)}
              >
                <option value="">All Cuisines</option>
                {cuisineTypes.map((cuisine) => (
                  <option key={cuisine} value={cuisine}>
                    {cuisine}
                  </option>
                ))}
              </select>
            </FilterGroup>
          </FiltersRow>
        </FiltersSection>

        <VendorsTable>
          <TableHeader>
            <div>Vendor</div>
            <div>Status</div>
            <div>Login Status</div>
            <div>Contact</div>
            <div>Rating</div>
            <div>Orders</div>
            <div>Revenue</div>
            <div>Actions</div>
          </TableHeader>

          {vendorsLoading ? (
            <LoadingState>
              <h3>Loading vendors...</h3>
            </LoadingState>
          ) : vendorsError ? (
            <EmptyState>
              <h3>Error loading vendors</h3>
              <p>{vendorsError.message}</p>
            </EmptyState>
          ) : filteredVendors.length === 0 ? (
            <EmptyState>
              <h3>No vendors found</h3>
              <p>Try adjusting your search criteria.</p>
            </EmptyState>
          ) : (
            filteredVendors.map((vendor, index) => (
              <React.Fragment key={vendor.id}>
                <VendorRow
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <VendorInfo>
                    <div className="vendor-avatar">
                      {vendor.businessName.charAt(0)}
                    </div>
                    <div className="vendor-details">
                      <h3>{vendor.businessName}</h3>
                      <div className="cuisine">{vendor.cuisineType}</div>
                    </div>
                  </VendorInfo>

                  <StatusBadge className={vendor.status}>
                    {vendor.status}
                  </StatusBadge>

                  <LoginStatusBadge
                    className={
                      vendor.hasLoggedIn ? "logged-in" : "not-logged-in"
                    }
                  >
                    {vendor.hasLoggedIn ? "Logged In" : "Not Logged In"}
                  </LoginStatusBadge>

                  <ContactInfo>
                    <div className="contact-item">
                      <FiMapPin />
                      {vendor.businessAddress}
                    </div>
                    <div className="contact-item">
                      <FiPhone />
                      {vendor.businessPhone}
                    </div>
                  </ContactInfo>

                  <StatsInfo>
                    <div className="stat-item">
                      <FiTrendingUp />
                      {vendor.rating.toFixed(1)} ({vendor.totalReviews})
                    </div>
                  </StatsInfo>

                  <StatsInfo>
                    <div className="stat-item">
                      <FiUsers />
                      {vendor.totalOrders}
                    </div>
                  </StatsInfo>

                  <StatsInfo>
                    <div className="stat-item">
                      <FiDollarSign />
                      {vendor.totalRevenue}
                    </div>
                  </StatsInfo>

                  <ActionsColumn>
                    <ActionIconButton
                      title="View Details"
                      onClick={() => handleVendorAction(vendor.id, "view")}
                    >
                      <FiEye />
                    </ActionIconButton>
                    <ActionIconButton
                      title="Edit Vendor"
                      onClick={() => handleVendorAction(vendor.id, "edit")}
                    >
                      <FiEdit />
                    </ActionIconButton>
                    <ActionIconButton
                      className="email"
                      title="Send Welcome Email"
                      onClick={() =>
                        handleVendorAction(vendor.id, "send-email")
                      }
                    >
                      <FiSend />
                    </ActionIconButton>
                    {vendor.status === "pending" && (
                      <>
                        <ActionIconButton
                          className="approve"
                          title="Approve Vendor"
                          onClick={() =>
                            handleVendorAction(vendor.id, "approve")
                          }
                        >
                          <FiCheckCircle />
                        </ActionIconButton>
                        <ActionIconButton
                          className="reject"
                          title="Reject Vendor"
                          onClick={() =>
                            handleVendorAction(vendor.id, "reject")
                          }
                        >
                          <FiXCircle />
                        </ActionIconButton>
                      </>
                    )}
                    {canDelete && (
                      <ActionIconButton
                        className="delete"
                        title="Delete Vendor"
                        onClick={() => handleVendorAction(vendor.id, "delete")}
                      >
                        <FiTrash2 />
                      </ActionIconButton>
                    )}
                  </ActionsColumn>
                </VendorRow>

                <MobileVendorCard>
                  <div className="card-header">
                    <VendorInfo>
                      <div className="vendor-avatar">
                        {vendor.businessName.charAt(0)}
                      </div>
                      <div className="vendor-details">
                        <h3>{vendor.businessName}</h3>
                        <div className="cuisine">{vendor.cuisineType}</div>
                      </div>
                    </VendorInfo>
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        flexWrap: "wrap",
                      }}
                    >
                      <StatusBadge className={vendor.status}>
                        {vendor.status}
                      </StatusBadge>
                      <LoginStatusBadge
                        className={
                          vendor.hasLoggedIn ? "logged-in" : "not-logged-in"
                        }
                      >
                        {vendor.hasLoggedIn ? "Logged In" : "Not Logged In"}
                      </LoginStatusBadge>
                    </div>
                  </div>

                  <div className="card-content">
                    <ContactInfo>
                      <div className="contact-item">
                        <FiMapPin />
                        {vendor.businessAddress}
                      </div>
                      <div className="contact-item">
                        <FiPhone />
                        {vendor.businessPhone}
                      </div>
                    </ContactInfo>

                    <StatsInfo>
                      <div className="stat-item">
                        <FiTrendingUp />
                        Rating: {vendor.rating.toFixed(1)} (
                        {vendor.totalReviews})
                      </div>
                      <div className="stat-item">
                        <FiUsers />
                        Orders: {vendor.totalOrders}
                      </div>
                      <div className="stat-item">
                        <FiDollarSign />
                        Revenue: {vendor.totalRevenue}
                      </div>
                    </StatsInfo>
                  </div>

                  <div className="card-actions">
                    <ActionIconButton
                      title="View Details"
                      onClick={() => handleVendorAction(vendor.id, "view")}
                    >
                      <FiEye />
                    </ActionIconButton>
                    <ActionIconButton
                      title="Edit Vendor"
                      onClick={() => handleVendorAction(vendor.id, "edit")}
                    >
                      <FiEdit />
                    </ActionIconButton>
                    <ActionIconButton
                      className="email"
                      title="Send Welcome Email"
                      onClick={() =>
                        handleVendorAction(vendor.id, "send-email")
                      }
                    >
                      <FiSend />
                    </ActionIconButton>
                    {vendor.status === "pending" && (
                      <>
                        <ActionIconButton
                          className="approve"
                          title="Approve Vendor"
                          onClick={() =>
                            handleVendorAction(vendor.id, "approve")
                          }
                        >
                          <FiCheckCircle />
                        </ActionIconButton>
                        <ActionIconButton
                          className="reject"
                          title="Reject Vendor"
                          onClick={() =>
                            handleVendorAction(vendor.id, "reject")
                          }
                        >
                          <FiXCircle />
                        </ActionIconButton>
                      </>
                    )}
                    {canDelete && (
                      <ActionIconButton
                        className="delete"
                        title="Delete Vendor"
                        onClick={() => handleVendorAction(vendor.id, "delete")}
                      >
                        <FiTrash2 />
                      </ActionIconButton>
                    )}
                  </div>
                </MobileVendorCard>
              </React.Fragment>
            ))
          )}
        </VendorsTable>

        {/* Add Vendor Modal */}
        {showAddModal && (
          <ModalOverlay onClick={handleCloseModal}>
            <Modal
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <h2>Add New Vendor</h2>
                <button className="close-button" onClick={handleCloseModal}>
                  <FiX />
                </button>
              </ModalHeader>

              <ModalContent>
                <Form onSubmit={handleSubmit}>
                  <div
                    style={{
                      marginBottom: "1rem",
                      padding: "1rem",
                      background: "#f0f4f1",
                      borderRadius: "8px",
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        color: "#2d5016",
                        fontSize: "0.9rem",
                      }}
                    >
                      💡 The vendor will complete their profile details when
                      they log in for the first time.
                    </p>
                  </div>

                  <FormGroup>
                    <label htmlFor="businessName">Business Name *</label>
                    <input
                      type="text"
                      id="businessName"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleInputChange}
                      className={errors.businessName ? "error" : ""}
                      placeholder="Enter business name"
                    />
                    {errors.businessName && (
                      <div className="error-message">{errors.businessName}</div>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="businessEmail">Business Email *</label>
                    <input
                      type="email"
                      id="businessEmail"
                      name="businessEmail"
                      value={formData.businessEmail}
                      onChange={handleInputChange}
                      className={errors.businessEmail ? "error" : ""}
                      placeholder="business@example.com"
                    />
                    {errors.businessEmail && (
                      <div className="error-message">
                        {errors.businessEmail}
                      </div>
                    )}
                    <small
                      style={{
                        color: "#666",
                        fontSize: "0.875rem",
                        marginTop: "0.25rem",
                        display: "block",
                      }}
                    >
                      This email will be used for the vendor account login
                    </small>
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="businessPhone">Business Phone *</label>
                    <input
                      type="tel"
                      id="businessPhone"
                      name="businessPhone"
                      value={formData.businessPhone}
                      onChange={handleInputChange}
                      className={errors.businessPhone ? "error" : ""}
                      placeholder="+592 123-4567"
                    />
                    {errors.businessPhone && (
                      <div className="error-message">
                        {errors.businessPhone}
                      </div>
                    )}
                  </FormGroup>
                </Form>
              </ModalContent>

              <ModalFooter>
                <Button type="button" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  primary
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create Vendor"}
                </Button>
              </ModalFooter>
            </Modal>
          </ModalOverlay>
        )}

        {/* View Vendor Modal */}
        {showViewModal && selectedVendor && (
          <ModalOverlay onClick={handleCloseModal}>
            <Modal
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <h2>Vendor Details</h2>
                <button className="close-button" onClick={handleCloseModal}>
                  <FiX />
                </button>
              </ModalHeader>

              <ModalContent>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1.5rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      padding: "1rem",
                      background: "#f8f9fa",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #2d5016, #4a7c59)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontWeight: "600",
                        fontSize: "1.5rem",
                      }}
                    >
                      {selectedVendor.businessName.charAt(0)}
                    </div>
                    <div>
                      <h3
                        style={{
                          margin: 0,
                          color: "#2d5016",
                          fontSize: "1.5rem",
                        }}
                      >
                        {selectedVendor.businessName}
                      </h3>
                      <p style={{ margin: "0.25rem 0", color: "#666" }}>
                        {selectedVendor.cuisineType} •{" "}
                        {selectedVendor.businessType}
                      </p>
                      <StatusBadge
                        className={selectedVendor.status}
                        style={{ marginTop: "0.5rem" }}
                      >
                        {selectedVendor.status}
                      </StatusBadge>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "1.5rem",
                    }}
                  >
                    <div>
                      <h4 style={{ color: "#2d5016", marginBottom: "1rem" }}>
                        Business Information
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.75rem",
                        }}
                      >
                        <div>
                          <strong>Email:</strong> {selectedVendor.businessEmail}
                        </div>
                        <div>
                          <strong>Phone:</strong> {selectedVendor.businessPhone}
                        </div>
                        <div>
                          <strong>Address:</strong>{" "}
                          {selectedVendor.businessAddress}
                        </div>
                        {selectedVendor.description && (
                          <div>
                            <strong>Description:</strong>{" "}
                            {selectedVendor.description}
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <h4 style={{ color: "#2d5016", marginBottom: "1rem" }}>
                        Owner Information
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.75rem",
                        }}
                      >
                        <div>
                          <strong>Name:</strong>{" "}
                          {selectedVendor.ownerName || "N/A"}
                        </div>
                        <div>
                          <strong>Email:</strong>{" "}
                          {selectedVendor.ownerEmail || "N/A"}
                        </div>
                        <div>
                          <strong>Phone:</strong>{" "}
                          {selectedVendor.ownerPhone || "N/A"}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 style={{ color: "#2d5016", marginBottom: "1rem" }}>
                      Performance Stats
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: "1rem",
                      }}
                    >
                      <div
                        style={{
                          textAlign: "center",
                          padding: "1rem",
                          background: "#f8f9fa",
                          borderRadius: "8px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "1.5rem",
                            fontWeight: "600",
                            color: "#2d5016",
                          }}
                        >
                          {selectedVendor.rating.toFixed(1)}
                        </div>
                        <div style={{ fontSize: "0.875rem", color: "#666" }}>
                          Rating
                        </div>
                      </div>
                      <div
                        style={{
                          textAlign: "center",
                          padding: "1rem",
                          background: "#f8f9fa",
                          borderRadius: "8px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "1.5rem",
                            fontWeight: "600",
                            color: "#2d5016",
                          }}
                        >
                          {selectedVendor.totalReviews}
                        </div>
                        <div style={{ fontSize: "0.875rem", color: "#666" }}>
                          Reviews
                        </div>
                      </div>
                      <div
                        style={{
                          textAlign: "center",
                          padding: "1rem",
                          background: "#f8f9fa",
                          borderRadius: "8px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "1.5rem",
                            fontWeight: "600",
                            color: "#2d5016",
                          }}
                        >
                          {selectedVendor.totalOrders}
                        </div>
                        <div style={{ fontSize: "0.875rem", color: "#666" }}>
                          Orders
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ModalContent>

              <ModalFooter>
                <Button onClick={handleCloseModal}>Close</Button>
                <Button
                  primary
                  onClick={() => {
                    setShowViewModal(false);
                    handleVendorAction(selectedVendor.id, "edit");
                  }}
                >
                  <FiEdit />
                  Edit Vendor
                </Button>
              </ModalFooter>
            </Modal>
          </ModalOverlay>
        )}

        {/* Edit Vendor Modal */}
        {showEditModal && selectedVendor && (
          <ModalOverlay onClick={handleCloseModal}>
            <Modal
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <h2>Edit Vendor</h2>
                <button className="close-button" onClick={handleCloseModal}>
                  <FiX />
                </button>
              </ModalHeader>

              <ModalContent>
                <Form onSubmit={handleSubmit}>
                  <FormRow>
                    <FormGroup>
                      <label htmlFor="edit-businessName">Business Name *</label>
                      <input
                        type="text"
                        id="edit-businessName"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleInputChange}
                        className={errors.businessName ? "error" : ""}
                        placeholder="Enter business name"
                      />
                      {errors.businessName && (
                        <div className="error-message">
                          {errors.businessName}
                        </div>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <label htmlFor="edit-businessType">Business Type *</label>
                      <select
                        id="edit-businessType"
                        name="businessType"
                        value={formData.businessType}
                        onChange={handleInputChange}
                        className={errors.businessType ? "error" : ""}
                      >
                        <option value="">Select business type</option>
                        {businessTypes.map((type) => (
                          <option key={type} value={type.toLowerCase()}>
                            {type}
                          </option>
                        ))}
                      </select>
                      {errors.businessType && (
                        <div className="error-message">
                          {errors.businessType}
                        </div>
                      )}
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <label htmlFor="edit-cuisineType">Cuisine Type *</label>
                      <select
                        id="edit-cuisineType"
                        name="cuisineType"
                        value={formData.cuisineType}
                        onChange={handleInputChange}
                        className={errors.cuisineType ? "error" : ""}
                      >
                        <option value="">Select cuisine type</option>
                        {cuisineTypes.map((cuisine) => (
                          <option key={cuisine} value={cuisine}>
                            {cuisine}
                          </option>
                        ))}
                      </select>
                      {errors.cuisineType && (
                        <div className="error-message">
                          {errors.cuisineType}
                        </div>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <label htmlFor="edit-businessEmail">
                        Business Email *
                      </label>
                      <input
                        type="email"
                        id="edit-businessEmail"
                        name="businessEmail"
                        value={formData.businessEmail}
                        onChange={handleInputChange}
                        className={errors.businessEmail ? "error" : ""}
                        placeholder="business@example.com"
                      />
                      {errors.businessEmail && (
                        <div className="error-message">
                          {errors.businessEmail}
                        </div>
                      )}
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <label htmlFor="edit-businessPhone">
                        Business Phone *
                      </label>
                      <input
                        type="tel"
                        id="edit-businessPhone"
                        name="businessPhone"
                        value={formData.businessPhone}
                        onChange={handleInputChange}
                        className={errors.businessPhone ? "error" : ""}
                        placeholder="+592 123-4567"
                      />
                      {errors.businessPhone && (
                        <div className="error-message">
                          {errors.businessPhone}
                        </div>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <label htmlFor="edit-businessAddress">
                        Business Address *
                      </label>
                      <input
                        type="text"
                        id="edit-businessAddress"
                        name="businessAddress"
                        value={formData.businessAddress}
                        onChange={handleInputChange}
                        className={errors.businessAddress ? "error" : ""}
                        placeholder="Enter full business address"
                      />
                      {errors.businessAddress && (
                        <div className="error-message">
                          {errors.businessAddress}
                        </div>
                      )}
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <label htmlFor="edit-description">
                      Business Description
                    </label>
                    <textarea
                      id="edit-description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Describe the business, specialties, etc."
                    />
                  </FormGroup>

                  <h3 style={{ margin: "1rem 0 0.5rem", color: "#2d5016" }}>
                    Owner Information
                  </h3>

                  <FormRow>
                    <FormGroup>
                      <label htmlFor="edit-ownerName">Owner Name *</label>
                      <input
                        type="text"
                        id="edit-ownerName"
                        name="ownerName"
                        value={formData.ownerName}
                        onChange={handleInputChange}
                        className={errors.ownerName ? "error" : ""}
                        placeholder="Enter owner's full name"
                      />
                      {errors.ownerName && (
                        <div className="error-message">{errors.ownerName}</div>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <label htmlFor="edit-ownerEmail">Owner Email *</label>
                      <input
                        type="email"
                        id="edit-ownerEmail"
                        name="ownerEmail"
                        value={formData.ownerEmail}
                        onChange={handleInputChange}
                        className={errors.ownerEmail ? "error" : ""}
                        placeholder="owner@example.com"
                      />
                      {errors.ownerEmail && (
                        <div className="error-message">{errors.ownerEmail}</div>
                      )}
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <label htmlFor="edit-ownerPhone">Owner Phone *</label>
                    <input
                      type="tel"
                      id="edit-ownerPhone"
                      name="ownerPhone"
                      value={formData.ownerPhone}
                      onChange={handleInputChange}
                      className={errors.ownerPhone ? "error" : ""}
                      placeholder="+592 123-4567"
                    />
                    {errors.ownerPhone && (
                      <div className="error-message">{errors.ownerPhone}</div>
                    )}
                  </FormGroup>
                </Form>
              </ModalContent>

              <ModalFooter>
                <Button type="button" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  primary
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </ModalFooter>
            </Modal>
          </ModalOverlay>
        )}
      </VendorManagementContent>
    </VendorManagementContainer>
  );
};

export default VendorManagement;
